import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { Plus, UserCog, UserX, KeyRound, Trash2, AlertTriangle, Archive } from "lucide-react";

const STATUS_TABS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "archived", label: "Archived" },
  { value: "all", label: "All" },
];

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("active");
  const [deleteTarget, setDeleteTarget] = useState(null);  // user object pending permanent delete

  const isAdmin = me?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([
        api.get("/users", { params: { status: statusFilter } }),
        api.get("/admin/roles"),
      ]);
      setUsers(u.data);
      setRoles(r.data.filter((x) => x.active));
    } catch (e) {
      toast.error("Failed to load users", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const handleDeactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.name}?\n\nThe user will lose access immediately but their history is preserved.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.name} deactivated`);
      load();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  const handlePermanentDelete = async () => {
    if (!deleteTarget) return;
    try {
      const { data } = await api.delete(`/users/${deleteTarget.id}/permanent`);
      if (data.mode === "archived") {
        toast.success(`${deleteTarget.name} archived (had history: ${data.reasons.join(", ")})`,
                      { description: "Archived users are hidden from normal lists and cannot log in. Their name is preserved in audit logs." });
      } else {
        toast.success(`${deleteTarget.name} permanently deleted`);
      }
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Users</h1>
          <p className="text-sm text-slate-500 mt-1">{users.length} {statusFilter} user{users.length === 1 ? "" : "s"} · soft-deactivate keeps audit history; permanent delete is admin-only.</p>
        </div>
        <UserDialog mode="create" roles={roles} onDone={load} />
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="user-status-tabs">
        {STATUS_TABS.map((t) => (
          <Button key={t.value}
                  variant={statusFilter === t.value ? "default" : "outline"}
                  className={statusFilter === t.value ? "bg-slate-900 text-white" : ""}
                  onClick={() => setStatusFilter(t.value)}
                  data-testid={`user-tab-${t.value}`}>
            {t.label}
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading && <div className="p-4 text-sm text-slate-500">Loading...</div>}
          <table className="w-full dense-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Name</th>
                <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Email</th>
                <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Phone</th>
                <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Role</th>
                <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Status</th>
                <th className="text-right text-xs uppercase tracking-wide text-slate-600 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && users.length === 0 && (
                <tr><td colSpan={6} className="py-12 text-center text-slate-400">No users in this view.</td></tr>
              )}
              {users.map((u) => {
                const archived = !!u.archived;
                const isMe = u.id === me?.id;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.email}`}>
                    <td className="text-slate-900 font-medium">
                      {u.name}
                      {isMe && <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-600">you</span>}
                      {archived && <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500 inline-flex items-center gap-0.5"><Archive size={10} />Archived</span>}
                    </td>
                    <td className="text-slate-700 font-mono text-xs">{u.email}</td>
                    <td className="text-slate-600 text-xs">{u.phone || "—"}</td>
                    <td className="text-slate-700">{u.role}</td>
                    <td>
                      {archived
                        ? <span className="text-xs text-amber-700 font-medium">Archived</span>
                        : u.active
                          ? <span className="text-xs text-emerald-700 font-medium">Active</span>
                          : <span className="text-xs text-slate-400">Inactive</span>}
                    </td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1">
                        {!archived && <UserDialog mode="edit" user={u} roles={roles} onDone={load} />}
                        {u.active && !isMe && (
                          <Button variant="outline" size="sm" onClick={() => handleDeactivate(u)} data-testid={`deactivate-${u.email}`}>
                            <UserX size={12} className="mr-1" /> Deactivate
                          </Button>
                        )}
                        {isAdmin && !u.active && !archived && !isMe && (
                          <Button variant="destructive" size="sm"
                                  className="bg-rose-600 hover:bg-rose-700 text-white"
                                  onClick={() => setDeleteTarget(u)}
                                  data-testid={`permanent-delete-${u.email}`}>
                            <Trash2 size={12} className="mr-1" /> Permanent Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <PermanentDeleteDialog target={deleteTarget}
                              onClose={() => setDeleteTarget(null)}
                              onConfirm={handlePermanentDelete} />
    </div>
  );
}


function PermanentDeleteDialog({ target, onClose, onConfirm }) {
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="permanent-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle size={18} /> Permanently delete user?
          </DialogTitle>
          <DialogDescription>
            This will permanently delete the user and cannot be undone.
          </DialogDescription>
        </DialogHeader>
        {target && (
          <div className="space-y-2 text-sm">
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
              <div className="font-semibold">{target.name}</div>
              <div className="text-xs font-mono text-slate-600">{target.email}</div>
              <div className="text-xs text-slate-500">Role: {target.role}</div>
            </div>
            <div className="text-xs text-slate-600 leading-relaxed">
              If this user is referenced by audit logs, complaints, or other historical records,
              the system will <b>archive</b> them instead (their name stays visible in old records,
              but they cannot log in and they disappear from normal lists). If there are no
              references, the row is <b>hard deleted</b>.
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cancel-perm-delete">Cancel</Button>
          <Button onClick={onConfirm}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="confirm-perm-delete">
            <Trash2 size={14} className="mr-1" /> Permanently Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function UserDialog({ mode, user, roles, onDone }) {
  const isEdit = mode === "edit";
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [role, setRole] = useState(user?.role || "");
  const [password, setPassword] = useState("");
  const [active, setActive] = useState(user?.active ?? true);
  const [allPerms, setAllPerms] = useState({ modules: [] });
  const [extras, setExtras] = useState(new Set(user?.extra_permissions || []));

  useEffect(() => {
    if (open) {
      setName(user?.name || "");
      setEmail(user?.email || "");
      setPhone(user?.phone || "");
      setRole(user?.role || "");
      setActive(user?.active ?? true);
      setPassword("");
      setExtras(new Set(user?.extra_permissions || []));
      api.get("/admin/permissions/catalog").then((r) => setAllPerms(r.data));
    }
  }, [open, user]);

  const toggleExtra = (p) => {
    const n = new Set(extras);
    if (n.has(p)) n.delete(p); else n.add(p);
    setExtras(n);
  };

  const submit = async () => {
    if (!name.trim() || !role) { toast.error("Name and role required"); return; }
    if (!email.trim() && !phone.trim()) { toast.error("Either email or phone is required"); return; }
    if (!isEdit && password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    try {
      if (isEdit) {
        const payload = { name, email: email || null, phone, role, active, extra_permissions: Array.from(extras) };
        if (password) payload.new_password = password;
        await api.put(`/users/${user.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", { name, email: email || undefined, phone, role, password });
        toast.success("User created");
      }
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit
          ? <Button variant="outline" size="sm" data-testid={`edit-user-${user.email}`}><UserCog size={12} className="mr-1" /> Edit</Button>
          : <Button className="bg-slate-900 text-white" data-testid="new-user-btn"><Plus size={14} className="mr-1" /> New User</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? `Edit ${user.name}` : "Create user"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="user-name-input" /></div>
            <div><Label>Email <span className="text-[10px] text-slate-400">(optional)</span></Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="user-email-input" /></div>
            <div><Label>Phone <span className="text-[10px] text-slate-400">(login alternative)</span></Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="user-phone-input" /></div>
            <div>
              <Label>Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="user-role-select"><SelectValue placeholder="Pick a role" /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{isEdit ? <span className="flex items-center gap-1"><KeyRound size={12} /> Reset password (leave blank to keep)</span> : "Password"}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="user-password-input" />
            </div>
            {isEdit && (
              <div className="flex items-end gap-2">
                <Checkbox checked={active} onCheckedChange={(v) => setActive(!!v)} data-testid="user-active-checkbox" />
                <Label>Active</Label>
              </div>
            )}
          </div>
          {isEdit && (
            <div>
              <Label>Extra permissions <span className="text-[10px] text-slate-400">(grant on top of role)</span></Label>
              <div className="mt-1 grid grid-cols-2 md:grid-cols-3 gap-1 max-h-56 overflow-y-auto border border-slate-200 rounded-md p-2">
                {(allPerms.modules || []).flatMap((m) =>
                  m.actions.map((a) => {
                    const p = `${m.key}.${a.key}`;
                    return (
                      <label key={p} className="flex items-center gap-2 text-xs cursor-pointer">
                        <Checkbox checked={extras.has(p)} onCheckedChange={() => toggleExtra(p)} data-testid={`extra-perm-${p}`} />
                        <span className="font-mono">{p}</span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-slate-900 text-white" data-testid="user-save-btn">
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
